import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Engine } from '@rossum/api-client/engines';
import { Tile } from '@rossum/components/tile';
import { useQueuesForEngine } from '../hooks/useQueuesForEngine';
import { engineBasicSettingsPath } from '../routes';

const VISIBLE_CHIPS_COUNT = 3;

type EngineCardProps = {
  engine: Engine;
};

const AuroraEngineTile = ({ engine }: EngineCardProps) => {
  const intl = useIntl();

  const { data, isFetching } = useQueuesForEngine({
    engine: [engine.id],
  });

  const queuesInfo = useMemo(
    () => ({
      info: data?.info ?? [],
      totalCount: data?.pagination?.total ?? 0,
    }),
    [data]
  );

  const queueChips = useMemo(() => {
    if (isFetching) {
      return [
        { key: 'skeleton-1', label: '', isLoading: true },
        { key: 'skeleton-2', label: '', isLoading: true },
      ];
    }

    if (queuesInfo.info.length === 0) {
      return [
        {
          key: 'empty',
          label: intl.formatMessage({
            id: 'features.engines.engineDetail.noQueue',
          }),
          color: 'transparent' as const,
        },
      ];
    }

    const remainingCount = queuesInfo.totalCount - VISIBLE_CHIPS_COUNT;

    const visibleChips = queuesInfo.info
      .slice(0, VISIBLE_CHIPS_COUNT)
      .map(info => ({
        key: String(info.id),
        label: `${info.workspace} - ${info.queue}`,
        tooltip: `${info.workspace} - ${info.queue}`,
      }));

    return [
      ...visibleChips,
      ...(remainingCount > 0
        ? [
            {
              key: 'more',
              label: `+${remainingCount}`,
              tooltip: queuesInfo.info
                .slice(VISIBLE_CHIPS_COUNT)
                .map(info => `${info.workspace} - ${info.queue}`)
                .join(', '),
            },
          ]
        : []),
    ];
  }, [queuesInfo, isFetching, intl]);

  return (
    <Tile
      title={engine.name}
      description={engine.description}
      chips={queueChips}
      to={engineBasicSettingsPath(engine.id)}
      data-cy="engine-tile"
      data-id={String(engine.id)}
    />
  );
};

export { AuroraEngineTile };
